import React, { useState, useEffect, useRef } from 'react' 
import { useNavigate } from 'react-router-dom';
import { takeReturnTo } from 'src/helpers/ReturnTo';
import ChangePassword from './ChangePassword';
import '../css/MFA.css';

const MultifactorAuth = ({site, token, setToken, setIsLoading, setError, loginMessage, userName, password, taxBridge = false}) => {

	const navigate = useNavigate();
	const API_BASE_URL = process.env.NODE_ENV === 'development' ? 
								process.env.REACT_APP_BASE_URL : window.API_URL;
	const inputRef = useRef(null);
	const [mfaName, setMfaName] = useState("");
	const [mfaCode, setMfaCode] = useState("");
	const [passwordExpired, setPasswordExpired] = useState(false);
	const lockedMessage = "Too many incorrect codes. Please wait 15 minutes, then click \"Send Again\" for a new code.";

	useEffect(() => {
		if(site && userName)
			setMfaName(`mfa-${site.toLowerCase()}-${userName.toLowerCase()}`);
	}, [site, userName]);

	useEffect(() => {
		if(inputRef.current)
			inputRef.current.focus();

		// Check if the site (vendor/client) and token has been passed along correctly
		if (site && token && userName) {
			const cName = `mfa-${site.toLowerCase()}-${userName.toLowerCase()}`;

			// For USCIS, check if MFA is needed first
			if (site.toLowerCase() === 'uscis') {
				fetch(API_BASE_URL + `/web/${site}/mfa/needed`,
					{
						method: "POST",
						headers: { 'Authorization': 'Bearer ' + token }
					})
					.then(async (response) => {		
						const text = await response.text();
						const data = text && JSON.parse(text);
						// If the validation is successful
						if (data && data.length > 0 && data[0].text === 'Not Needed') {
							validate(cName, '111111', true);
						} else {
							// MFA is needed, proceed with normal flow
							if (localStorage.getItem(cName) !== null)
								validate(cName, localStorage.getItem(cName), true);
							else
								generate();
						}
					})
					.catch((err) => {
						console.log(err);
						// On error, proceed with normal MFA flow
						if (localStorage.getItem(cName) !== null)
							validate(cName, localStorage.getItem(cName), true);
						else
							generate();
					});
			} else {
				// For non-USCIS sites, use existing logic
				// If the session has an existing mfa code
				if (localStorage.getItem(cName) !== null)
					validate(cName, localStorage.getItem(cName), true);
				else
					generate();
			}
		// Login again if either the site type or token was lost
		} else {
			navigate('/',
				{ state: { error: 'Your session has expired. Please Login again' }});
		}
	}, []);

	const validate = (cookieName, code, sysload = false) => {
		if (code) {
			if(sysload) setIsLoading(true);
			fetch(API_BASE_URL + `/web/${site}/mfa/validate/${code}`,
				{ 
					method: "POST",
					headers: { 'Authorization': 'Bearer ' + token }
				})
				.then(async (response) => {
						const text = await response.text();
						const data = text && JSON.parse(text);
						// If the validation is successful
						if (data && data.length > 0 && data[0].text === 'Passed') {
							// The emailed code is single-use (GEX-6076): on its first redemption the
							// server mints a separate, never-emailed device-trust token (data[1]) for
							// the 30-day "remember this device" replay. Store that instead of the code
							// once it's issued; keep replaying the code itself when no token comes back
							// (this request was itself a trusted-device replay of an already-issued token).
							const newDeviceToken = data.length > 1 ? data[1].text : null;
							const deviceCredential = (newDeviceToken && newDeviceToken !== 'null') ? newDeviceToken : code;
							localStorage.setItem(cookieName, deviceCredential);

							// NCC-E034356-32D (response manipulation): the token from login never actually
							// completes MFA server-side on its own -- the server now reissues one carrying
							// "mfa_verified"=true (data[2]) on a real Passed result, and every other endpoint
							// refuses the pre-MFA token. This MUST replace it, not the original `token` prop.
							const verifiedToken = (data.length > 2 && data[2].text) ? data[2].text : token;
							localStorage.setItem("token", verifiedToken);
							setToken(verifiedToken);

							if(loginMessage === 'Success') {
								if (!taxBridge)
									navigate(takeReturnTo(site) || `/${site}/home`);
								else {
									if (site === 'client') {
										navigate('/client/file/upload');
									}
									else {
										navigate('/vendor/invoice/due');
									}
								}
							}
							else if(loginMessage === 'Password Expired'){
								setError("Your password has expired. Please enter a new password");
								setPasswordExpired(true);
							}
						} else if (data && data.length > 0 && data[0].text === 'Locked') {
							// NCC-E034356-32D rate limit: too many wrong codes; the server refuses both
							// validate and generate until the lockout window lapses.
							setError(lockedMessage);
						} else {
							if(sysload) 
								generate();
							else
								setError("Incorrect. Please try again with the most recent code.");
						}
						setIsLoading(false);
				})				
				.catch((err) => { 
					console.log(err);
					setError(err);
					setIsLoading(false);
				});
		}
	}

	const [recentlyGenerated, setRecentlyGenerated] = useState(false);

	const generate = () => {		
		if(!recentlyGenerated) {
			setIsLoading(true);
			setRecentlyGenerated(true);
			setTimeout(() => setRecentlyGenerated(false), 20000);
			fetch(API_BASE_URL + `/web/${site}/mfa/generate`,
				{ 
					method: "POST",
					headers: { 'Authorization': 'Bearer ' + token }
				})
				.then(async (response) => {
					setIsLoading(false);
					if(!response.ok) {
						setToken("");
						setError("Your session has expired. Please Login again");
					} else {
						const text = await response.text();
						const data = text && JSON.parse(text);
						setError((data && data.length > 0 && data[0].text === 'Locked') ? lockedMessage : "");
					}
				});
		}
	}

	if(!passwordExpired)
		return (
		<div id="mfaContainer" style={{minHeight:250}}>
			<table style={{ width: "100%", margin: "0 auto", borderCollapse: "collapse", padding: "10px" }}>
				<tbody>
					<tr>
						<td style={{ textAlign: "center" }}>
							<div className="note">A code has been sent to the email registered to this account. <br/>
							Please enter the code below to verify this device.<br/><br/>
							<small>If you do not receive an email within a few minutes, <br/>click "Send Again" to receive a new one.</small>
							</div>
						</td>
					</tr>                    
					<tr>
						<td style={{ padding: "10px", textAlign: "center" }}><br/>
							<input id="mfaCode"
								ref={inputRef}
								value={mfaCode}
								onChange={(event) => setMfaCode(event.target.value)}
								onKeyDown={(event) => { if(event.key == 'Enter') validate(mfaName, mfaCode); }}
								maxLength={8}
								placeholder="________"
								required={true}
							/>
						</td>
					</tr>
					<tr>
						<td style={{ textAlign: "center", paddingTop: "20px", paddingBottom: "20px" }}>
							<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
								<button className='blueBtn' onClick={() => validate(mfaName, mfaCode)}>Submit Code</button>
								<button className='redBtn' onClick={generate} disabled={recentlyGenerated}>Send Again</button>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
		);
	else
		return <ChangePassword audience={site} redirect={true} defaultPassword={password} />
}

export default MultifactorAuth;